
import {
  computed, useRoute, nextTick, ref, watch, onMounted
} from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore';
import useTracking from '~/composable/tracking/useTracking';
import useUtilities from '~/composable/useUtilities';
import { STOCK_STATUS } from '~/constants/StockStatus';
import useSavedUrls from '~/composable/useSavedUrls';
import { PURCHASE_CATEGORY } from '~/constants/PurchaseCategory';
import ProductDetailsVoucherCode from '~/components/Voucher/VoucherCode.vue';

export default {
  components: {
    'product-details-voucher-code': ProductDetailsVoucherCode
  },
  props: {
    offeredToCustomerType: { type: Array, default: () => [] },
    hasSpecialPrices: { type: Boolean, default: false }
  },
  setup () {
    const { formatPrice } = useUtilities()
    const chosenVariantDetailsStore = useChosenVariantDetailsStore()
    const {
      chosenStorage,
      chosenColor,
      chosenChannel,
      chosenInstallment,
      productDetails
    } = storeToRefs(chosenVariantDetailsStore)
    const { track } = useTracking()
    const { saveBackUrl } = useSavedUrls()
    const route = useRoute()
    const categorySlug = route.value.params.categoryslug.toLowerCase()
    const isAccessoryCategory = computed(() => categorySlug === 'tilbehoer')
    const expectedDeliveryDate = computed(() => productDetails?.value?.expectedDeliveryDate)
    const skuInStock = computed(() => productDetails?.value?.stockCheck)
    const purchaseCategory = computed(() => productDetails?.value?.purchaseCategory)
    const isDeviceOrAccessory = computed(() => {
      const productType = productDetails?.value?.chosenVariant?.productTypeKey
      return productType === 'device' || productType === 'accessory' || isAccessoryCategory.value
    })

    const hasSubscriptionOptions = computed(() => {
      return productDetails?.value?.chosenVariantPriceMatrix?.subscriptionBundleOptions.find(option => option.channelReferenceID !== null) !== undefined
    })

    const isStorageUpdateInProgress = ref(false)

    const addToBasketBtn = computed(() => {
      let btnText = skuInStock.value ? 'Køb' : 'Udsolgt'
      let btnVariant = skuInStock.value ? 'secondary' : 'gray'
      if (purchaseCategory.value === PURCHASE_CATEGORY.PRE_ORDER) {
        btnText = 'Forudbestil'; btnVariant = 'tertiary'
      } else if (purchaseCategory.value === PURCHASE_CATEGORY.BACK_ORDER) {
        btnText = 'Bestil'; btnVariant = 'tertiary'
      }
      return { btnText, btnVariant }
    })

    const lastStableColors = ref([])
    const lastStableActiveColor = ref('')
    const lastStableActiveColorName = ref('')

    watch(() => productDetails.value?.productColorVariants, (list) => {
      if (Array.isArray(list) && list.length) {
        lastStableColors.value = list.slice()
      }
    }, { immediate: true })

    watch(() => productDetails.value?.activeColor, (c) => {
      if (c) {
        lastStableActiveColor.value = c
      }
    }, { immediate: true })

    watch(() => productDetails.value?.activeColorName, (n) => {
      if (n) {
        lastStableActiveColorName.value = n
      }
    }, { immediate: true })

    const colorsForPicker = computed(() => {
      const list = productDetails.value?.productColorVariants
      return Array.isArray(list) && list.length ? list : lastStableColors.value
    })
    const activeColorForPicker = computed(() => productDetails.value?.activeColor || lastStableActiveColor.value)
    const activeColorNameForPicker = computed(() => productDetails.value?.activeColorName || lastStableActiveColorName.value)

    const updateChosenStorage = async (event) => {
      chosenColor.value = productDetails.value.activeColor
      chosenStorage.value = event
      isStorageUpdateInProgress.value = true
      await nextTick()
    }

    watch(chosenChannel, (newVal) => {
      if (isStorageUpdateInProgress.value) {
        const wrappedChosenChannel = { value: newVal }
        const categorySlug = route.value.params.categoryslug.toLowerCase()
        track('changeStorage', {
          storage: chosenStorage.value,
          productDetails,
          chosenInstallment,
          chosenChannel: wrappedChosenChannel,
          categorySlug
        })
        isStorageUpdateInProgress.value = false
      }
    })

    const updateChosenColor = async (color) => {
      chosenVariantDetailsStore.$patch((state) => {
        state.chosenStorage = productDetails.value.activeStorage
        state.chosenColor = color
      })
      await nextTick()
      track('changeColor', { color: productDetails.value.activeColorName, productDetails, chosenInstallment, chosenChannel })
    }

    // Ensure payment box shows even when only cash is available by defaulting channel to Cash
    const ensureDefaultCashChannel = () => {
      try {
        if (!chosenChannel.value && productDetails.value?.chosenVariantPriceMatrix?.cashOption) {
          chosenChannel.value = productDetails.value.chosenVariantPriceMatrix.cashOption
        }
      } catch (e) { /* no-op */ }
    }

    onMounted(() => {
      ensureDefaultCashChannel()
    })

    watch(() => productDetails.value?.chosenVariantPriceMatrix, () => {
      ensureDefaultCashChannel()
    })

    const trackAddToBasket = () => {
      track('addToBasket', { productDetails, chosenInstallment, chosenChannel, categorySlug, label: addToBasketBtn.value.btnText })
    }


    // Note: SKU change watcher removed - VoucherCode component already handles this
    // VoucherCode.vue has a watcher for chosenVariantSku that automatically reapplies voucher

    const isPreOrder = computed(() => purchaseCategory.value === PURCHASE_CATEGORY.PRE_ORDER)
    const isBackOrder = computed(() => purchaseCategory.value === PURCHASE_CATEGORY.BACK_ORDER)
    const isPreOrderOrBackOrder = computed(() => isPreOrder.value || isBackOrder.value)
    const channelChosen = (chosen) => { chosenChannel.value = chosen }

    const voucherCodeRef = ref(null)
    const removingDiscountCode = computed(() => voucherCodeRef.value?.removing || false)
    const removeDiscountCode = () => {
      voucherCodeRef.value?.removeVoucher?.()
    }

    return {
      categorySlug,
      saveBackUrl,
      hasSubscriptionOptions,
      channelChosen,
      isPreOrder,
      isBackOrder,
      isPreOrderOrBackOrder,
      STOCK_STATUS,
      expectedDeliveryDate,
      purchaseCategory,
      formatPrice,
      skuInStock,
      chosenChannel,
      chosenInstallment,
      productDetails,
      addToBasketBtn,
      trackAddToBasket,
      updateChosenStorage,
      updateChosenColor,
      PURCHASE_CATEGORY,
      colorsForPicker,
      activeColorForPicker,
      activeColorNameForPicker,
      isDeviceOrAccessory,
      voucherCodeRef,
      removingDiscountCode,
      removeDiscountCode
    }
  }
}
